import React, { useState } from 'react';
import TrackChangesRoundedIcon from '@mui/icons-material/TrackChangesRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import { Sprite, carFor } from '../Sprites/Sprite';
import './InflationEffect.scss';

/**
 * 「現金在手最大敵人：通脹」, an in-house replacement for the Piktochart of the same name,
 * in the look of ai.portfoplus.com: $1 million today, and what inflation leaves of it
 * after ten and twenty years, each bar drawn to scale with the part inflation has eaten
 * hatched above what is left. Purchasing power is discounted by compounded annual
 * price growth; a rate chip and a year slider let the adviser try other rates and ages.
 */

const RATES = [3, 4, 5, 6];
const DEFAULT_RATE = 3;
const MAX_YEARS = 30;
const DEFAULT_YEARS = 15;

/** Years from today, and the key of the label that goes with each bar. */
const BARS = [
  { years: 0, label: 'ife-today' },
  { years: 10, label: 'ife-10' },
  { years: 20, label: 'ife-20' }
];

/** What is left, as a share of today's money, after `years` years of `rate`% inflation. */
export const remaining = (years: number, rate = DEFAULT_RATE) => 1 / Math.pow(1 + rate / 100, years);

interface Intl {
  locale: string;
  formatMessage: (descriptor: { id: string }, values?: Record<string, string | number>) => string;
}

/** The app's `zh` is Simplified Chinese; `lang` picks the CJK font in the stylesheet. */
const HTML_LANG: Record<string, string> = { zh: 'zh-CN', 'zh-Hant-HK': 'zh-HK' };

const InflationEffect = ({ intl }: { intl: Intl }) => {
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const [rate, setRate] = useState(DEFAULT_RATE);
  const [years, setYears] = useState(DEFAULT_YEARS);

  /** The share in whole percent, so what is left and what is lost always add to the full amount. */
  const percent = (share: number) => Math.round(share * 100);
  /** "74萬" or "740k": `ife-scale` turns percent of $1M into the language's own unit. */
  const amount = (pct: number) => (pct >= 100 ? t('ife-full') : t('ife-amount', { n: pct * Number(t('ife-scale')) }));

  const tryLeft = percent(remaining(years, rate));

  return (
    <div className="inflation-effect" lang={HTML_LANG[intl.locale] || intl.locale}>
      <header className="ife-head">
        <h2 className="ife-title">
          <span>
            {t('ife-title')}
            <strong>{t('ife-title-key')}</strong>
          </span>
        </h2>
        <p className="ife-assume">{t('ife-assume', { rate })}</p>
        <p className="ife-eroded">{t('ife-eroded')}</p>
      </header>

      <div className="ife-rates" role="group" aria-label={t('ife-rate-label')}>
        <span>{t('ife-rate-label')}</span>
        {RATES.map(r => (
          <button
            key={r}
            type="button"
            className={r === rate ? 'is-on' : ''}
            aria-pressed={r === rate}
            onClick={() => setRate(r)}
          >
            {r}%
          </button>
        ))}
      </div>

      <figure className="ife-chart" role="img" aria-label={t('ife-chart-label')}>
        <div className="ife-plot">
          <span className="ife-tick is-top">{t('ife-full')}</span>
          <span className="ife-tick is-mid">{amount(50)}</span>
          {BARS.map(({ years: barYears }, i) => {
            const left = percent(remaining(barYears, rate));
            return (
              <div key={barYears} className="ife-col" style={{ '--i': i } as React.CSSProperties}>
                <div className="ife-stack">
                  {left < 100 && (
                    <div className="ife-lost" style={{ height: `${100 - left}%` }}>
                      <LocalFireDepartmentRoundedIcon aria-hidden="true" />
                      <span>{t('ife-lost', { n: amount(100 - left) })}</span>
                    </div>
                  )}
                  <div className={`ife-left${left < 100 ? ' is-burnt' : ''}`} style={{ height: `${left}%` }}>
                    {left < 100 && <span className="ife-char" aria-hidden="true" />}
                    <strong>${amount(left)}</strong>
                    {/* The same money as a car: whole today, in parts later, a single tire at the end. */}
                    <Sprite name={carFor(left / 100)} className="ife-car" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="ife-labels">
          {BARS.map(({ years: barYears, label }) => (
            <span key={barYears}>{t(label)}</span>
          ))}
        </div>
      </figure>

      <section className="ife-try" aria-live="polite">
        <label htmlFor="ife-years">
          <span>{t('ife-try')}</span>
          <strong>{t('ife-years', { y: years })}</strong>
        </label>
        <input
          id="ife-years"
          type="range"
          min={0}
          max={MAX_YEARS}
          value={years}
          onChange={event => setYears(Number(event.target.value))}
          style={{ '--fill': `${(years / MAX_YEARS) * 100}%` } as React.CSSProperties}
        />
        <div className="ife-meter" aria-hidden="true">
          <span className="ife-meter-left" style={{ width: `${tryLeft}%` }} />
          <span className="ife-meter-lost" style={{ width: `${100 - tryLeft}%` }} />
        </div>
        {/* The car you could buy with it, losing its parts as the years go. */}
        <div className="ife-result-row">
          <Sprite name={carFor(tryLeft / 100)} size={104} className="ife-try-car" />
          <p className="ife-result">
            {t('ife-result', { y: years, left: `$${amount(tryLeft)}`, lost: amount(100 - tryLeft) })}
          </p>
        </div>
      </section>

      <div className="ife-banner">
        <TrackChangesRoundedIcon aria-hidden="true" />
        <p>{t('ife-banner')}</p>
      </div>

      <p className="ife-act">
        {t('ife-act')}
        <strong>{t('ife-act-key')}</strong>
      </p>

      <p className="ife-disclaimer">
        {t('dsc-disclaimer-1')}
        <br />
        {t('dsc-disclaimer-2')}
      </p>
    </div>
  );
};

export default InflationEffect;
